/*
 * Copyright (c) 2024 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Component imports
import { Button, Col, Collapse, Form, Input, Row, Tooltip, Typography } from 'antd';
import { LoadingOutlined, CheckOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { ArrowRightIcon } from '../Icons/ArrowRightIcon';

// Misc imports
import { useTranslation } from 'react-i18next';
import { CORS_PROXY_URL } from '../../constants/env';
import { URL_REGEX } from '../../constants/regex';
import useDebouncedCallback from '../../utils/hooks/useDebounce';

const OpenIdConfigurationFormItem = ({ formSubmissionErrors }) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();

  const [configurationUrlStatus, setConfigurationUrlStatus] = useState(); // undefined, 'loading', 'error', 'success'
  const [configurationUrlStatusTooltip, setConfigurationUrlStatusTooltip] = useState();
  const [tooltipProps, setTooltipProps] = useState({});
  const [activeKey, setActiveKey] = useState();

  useEffect(() => {
    const fields = ['issuer', 'authorizationEndpoint', 'tokenEndpoint', 'jwksUri'];
    if (formSubmissionErrors?.errorFields?.some((field) => fields.includes(field.name[0]))) {
      setConfigurationUrlStatus('error');
      setConfigurationUrlStatusTooltip(t('Please, provide a valid URL pointing to an OpenID configuration.'));
      setTooltipProps({ open: true });
    }
  }, [formSubmissionErrors]);

  const showApiError = useCallback((msg) => {
    setConfigurationUrlStatus('error');
    setConfigurationUrlStatusTooltip(msg);
    setActiveKey(['1']);
    setTooltipProps({ open: true });
  }, []);

  const searchForOpenIdConfigurationInfo = useCallback((configUrl) => {
    const url = CORS_PROXY_URL ? CORS_PROXY_URL + '?' + encodeURIComponent(configUrl) : configUrl;

    const isUrl = URL_REGEX.test(configUrl);
    if (!isUrl) {
      const isEmpty = configUrl.trim() === '';
      setConfigurationUrlStatus(!isEmpty ? 'error' : undefined);
      setConfigurationUrlStatusTooltip(
        !isEmpty ? t('This is not a valid URL. Please, provide a valid URL pointing to an OpenID configuration.') : ''
      );
      setTooltipProps({});
      return;
    }

    setConfigurationUrlStatus('loading');
    setConfigurationUrlStatusTooltip(t('Fetching data from the configuration URL...'));
    setTooltipProps({});
    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        const { issuer, authorization_endpoint, token_endpoint, jwks_uri, userinfo_endpoint } = data;
        if (issuer && authorization_endpoint && token_endpoint && jwks_uri) {
          setConfigurationUrlStatus('success');
          setConfigurationUrlStatusTooltip('');
          setTooltipProps({});

          form.setFieldsValue({
            issuer,
            authorizationEndpoint: authorization_endpoint,
            tokenEndpoint: token_endpoint,
            jwksUri: jwks_uri,
            userInfoEndpoint: userinfo_endpoint || null
          });
        } else {
          showApiError(
            t(
              'The configuration URL does not contain the required fields. Please check the URL or fill the fields manually.'
            )
          );
        }
      })
      .catch((error) => {
        showApiError(
          t('Error fetching data from the configuration URL. Please check the URL or fill the fields manually.')
        );
      });
  }, [showApiError]);

  const debouncedOnChange = useDebouncedCallback(searchForOpenIdConfigurationInfo);

  return (
    <>
      <Form.Item name="configurationUrl" className="mb-0">
        <Input
          status={configurationUrlStatus === 'error' ? 'error' : undefined}
          onChange={(e) => debouncedOnChange(e.target.value)}
          suffix={
            <Tooltip title={configurationUrlStatusTooltip} {...tooltipProps}>
              {configurationUrlStatus === 'loading' ? (
                <LoadingOutlined />
              ) : configurationUrlStatus === 'error' ? (
                <Typography.Text type="danger">
                  <ExclamationCircleOutlined />
                </Typography.Text>
              ) : configurationUrlStatus === 'success' ? (
                <Typography.Text type="success">
                  <CheckOutlined />
                </Typography.Text>
              ) : null}
            </Tooltip>
          }
        />
      </Form.Item>
      <Collapse
        ghost
        className="mb-3"
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key)}
        expandIcon={({ isActive }) => (
          <Button type="link" size="small" className="mr-1 pr-0 pl-0 fs-sm">
            <ArrowRightIcon rotate={isActive ? 90 : 0} />
          </Button>
        )}>
        <Collapse.Panel
          className={'ml-1'}
          showArrow={true}
          forceRender={true}
          header={
            <Button type="link" size="small" className="ml-0 pl-0 fs-sm">
              {t('Show full configuration')}
            </Button>
          }
          key="1">
          <Row align="middle" wrap={false} className="mb-2 ml-2">
            <Col flex="0 1 464px" className="bg-layout border-radius-base px-5 py-2">
              <Typography.Paragraph className="mb-2">{t('Issuer')}</Typography.Paragraph>
              <Form.Item
                name={'issuer'}
                className="mb-2"
                rules={[
                  {
                    required: true,
                    message: t('Please enter the issuer')
                  }
                ]}>
                <Input placeholder={'http://idp.somurl.com'} />
              </Form.Item>

              <Typography.Paragraph className="mb-2">{t('Authorization Endpoint')}</Typography.Paragraph>
              <Form.Item
                name={'authorizationEndpoint'}
                className="mb-2"
                rules={[
                  {
                    required: true,
                    message: t('Please enter an authorization endpoint')
                  }
                ]}>
                <Input placeholder={'http://idp.somurl.com/connect/authorize'} />
              </Form.Item>

              <Typography.Paragraph className="mb-2">{t('Token Endpoint')}</Typography.Paragraph>
              <Form.Item
                name={'tokenEndpoint'}
                className="mb-2"
                rules={[
                  {
                    required: true,
                    message: t('Please enter a token endpoint')
                  }
                ]}>
                <Input placeholder={'http://idp.somurl.com/connect/token'} />
              </Form.Item>

              <Typography.Paragraph className="mb-2">{t('JWKS URI')}</Typography.Paragraph>
              <Form.Item
                name={'jwksUri'}
                className="mb-2"
                rules={[
                  {
                    required: true,
                    message: t('Please enter a JWKS URI')
                  }
                ]}>
                <Input placeholder={'http://idp.somurl.com/connect/jwks'} />
              </Form.Item>

              <Typography.Paragraph className="mb-2">{t('UserInfo Endpoint')}</Typography.Paragraph>
              <Form.Item name={'userInfoEndpoint'} className="mb-2">
                <Input placeholder={'http://idp.somurl.com/connect/userinfo'} />
              </Form.Item>
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>
    </>
  );
};

OpenIdConfigurationFormItem.propTypes = {
  formSubmissionErrors: PropTypes.any
};

export default OpenIdConfigurationFormItem;
